import React, { useState, ReactNode, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { toProfileFields } from '@/lib/profile-fields';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { translations, LanguageCode } from '@/translations/translations';
import { setSessionInvalidationHandler, sessionRefresher } from '@/api/api-client';
import { saveAuthReturnPath } from '@/lib/auth-return';
import type { ValidationGate } from '@/types/auth.types';
import { toGeolocationConsent } from '@/features/collection-place/collection-place.contract';

import {
  AppContext,
  type AppContextValue,
  type AppUser,
} from './app-context.shared';

type User = AppUser;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toStringOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const toBooleanOrUndefined = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const items = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  return items.length > 0 ? items : undefined;
};

const toValidationGateOrUndefined = (value: unknown): ValidationGate | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const isBlocked = toBooleanOrUndefined(value.isBlocked);
  const contributionsSinceLastValidation = toNumberOrUndefined(value.contributionsSinceLastValidation);
  if (typeof isBlocked !== 'boolean' || typeof contributionsSinceLastValidation !== 'number') {
    return undefined;
  }

  const blockThreshold = toNumberOrUndefined(value.blockThreshold) ?? 50;
  const warningThreshold = toNumberOrUndefined(value.warningThreshold) ?? 40;
  const validationsSinceBlock = toNumberOrUndefined(value.validationsSinceBlock) ?? 0;
  const validationsRequired = toNumberOrUndefined(value.validationsRequired) ?? 100;
  const blockedAt = toStringOrUndefined(value.blockedAt) ?? null;
  const exempt = toBooleanOrUndefined(value.exempt);

  return {
    isBlocked,
    contributionsSinceLastValidation,
    blockThreshold,
    warningThreshold,
    validationsSinceBlock,
    validationsRequired,
    blockedAt,
    ...(typeof exempt === 'boolean' ? { exempt } : {}),
  };
};

const toUserRoleOrUndefined = (value: unknown): User['role'] => {
  if (value === 'admin' || value === 'collector' || value === 'moderator' || value === 'user' || value === 'accountant' || value === 'super_admin') {
    return value;
  }

  if (value === 'SUPER_ADMIN') return 'super_admin';
  if (value === 'ADMIN') return 'admin';
  if (value === 'collecteur' || value === 'COLLECTOR' || value === 'COLLECTEUR' || value === 'ANNOTATOR' || value === 'ANNOTATEUR') return 'collector';
  if (value === 'MODERATOR') return 'moderator';
  if (value === 'USER') return 'user';
  if (value === 'COMPTABLE' || value === 'ACCOUNTANT') return 'accountant';

  return undefined;
};

const decodeJwtSubject = (token: string): string | undefined => {
  try {
    const [, payload] = token.split('.');
    if (!payload || typeof atob !== 'function') {
      return undefined;
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddingLength = normalized.length % 4 === 0 ? 0 : 4 - (normalized.length % 4);
    const padded = paddingLength ? normalized.padEnd(normalized.length + paddingLength, '=') : normalized;
    const decoded = atob(padded);
    const data = JSON.parse(decoded);
    return typeof data.sub === 'string' ? data.sub : undefined;
  } catch {
    return undefined;
  }
};

const extractToken = (record: Record<string, unknown>): string | undefined => {
  const tokenCandidates = [record.token, record.access_token, record.accessToken, record.jwt];
  for (const candidate of tokenCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  const nested = record.data;
  if (isRecord(nested)) {
    return extractToken(nested);
  }

  return undefined;
};

const extractUserId = (record: Record<string, unknown>): string | undefined => {
  const idCandidate =
    record.id ??
    record._id ??
    record.userId ??
    record.uid ??
    record.uuid ??
    record.sub;

  if (typeof idCandidate === 'string') {
    return idCandidate;
  }

  if (typeof idCandidate === 'number' && Number.isFinite(idCandidate)) {
    return String(idCandidate);
  }

  const token = extractToken(record);
  if (token) {
    return decodeJwtSubject(token);
  }

  return undefined;
};

const buildUserFromRecord = (record: Record<string, unknown>): User | null => {
  const id = extractUserId(record);
  if (!id) {
    return null;
  }

  const username =
    toStringOrUndefined(record.username) ??
    toStringOrUndefined(record.name) ??
    toStringOrUndefined(record.email) ??
    id;

  const user: User = {
    id,
    username,
  };

  const name = toStringOrUndefined(record.name);
  if (name) {
    user.name = name;
  }

  const email = toStringOrUndefined(record.email);
  if (email) {
    user.email = email;
  }

  const country = toStringOrUndefined(record.country);
  if (country) {
    user.country = country;
  }

  const gender = toStringOrUndefined(record.gender);
  if (gender) {
    user.gender = gender;
  }

  const phoneNumber = toStringOrUndefined(record.phoneNumber);
  if (phoneNumber) {
    user.phoneNumber = phoneNumber;
  }

  const role = toUserRoleOrUndefined(record.role);
  if (role) {
    user.role = role;
  }

  const spokenLanguages = toStringArray(record.spokenLanguages);
  if (spokenLanguages) {
    user.spokenLanguages = spokenLanguages;
  }

  const contributionLanguages = toStringArray(record.contributionLanguages);
  if (contributionLanguages) {
    user.contributionLanguages = contributionLanguages;
  }

  const moderationLanguages = toStringArray(record.moderationLanguages);
  if (moderationLanguages) {
    user.moderationLanguages = moderationLanguages;
  }

  if (record.preferredLocale === 'fr' || record.preferredLocale === 'en') {
    user.preferredLocale = record.preferredLocale;
  }

  const requiresLanguageSetup = toBooleanOrUndefined(
    record.requiresLanguageSetup,
  );
  if (typeof requiresLanguageSetup === 'boolean') {
    user.requiresLanguageSetup = requiresLanguageSetup;
  }

  const missingProfileFields = toProfileFields(record.missingProfileFields);
  if (missingProfileFields) {
    user.missingProfileFields = missingProfileFields;
  }

  const requiresProfileCompletion = toBooleanOrUndefined(
    record.requiresProfileCompletion,
  );
  if (typeof requiresProfileCompletion === 'boolean') {
    user.requiresProfileCompletion = requiresProfileCompletion;
  }

  const isActive = toBooleanOrUndefined(record.isActive);
  if (typeof isActive === 'boolean') {
    user.isActive = isActive;
  }

  const isAcceptedTerms = toBooleanOrUndefined(record.isAcceptedTerms);
  if (typeof isAcceptedTerms === 'boolean') {
    user.isAcceptedTerms = isAcceptedTerms;
  }

  const mustChangePassword = toBooleanOrUndefined(record.mustChangePassword);
  if (typeof mustChangePassword === 'boolean') {
    user.mustChangePassword = mustChangePassword;
  }

  const contributions = toNumberOrUndefined(record.contributionsCount);
  if (typeof contributions === 'number') {
    user.contributionsCount = contributions;
  }

  const createdAt = toStringOrUndefined(record.createdAt);
  if (createdAt) {
    user.createdAt = createdAt;
  }

  const age = toNumberOrUndefined(record.age);
  if (typeof age === 'number') {
    user.age = age;
  }

  const acceptedCount = toNumberOrUndefined(record.acceptedCount);
  if (typeof acceptedCount === 'number') {
    user.acceptedCount = acceptedCount;
  }

  const rejectedCount = toNumberOrUndefined(record.rejectedCount);
  if (typeof rejectedCount === 'number') {
    user.rejectedCount = rejectedCount;
  }

  const wantsToBePaid = toBooleanOrUndefined(record.wantsToBePaid);
  if (typeof wantsToBePaid === 'boolean') {
    user.wantsToBePaid = wantsToBePaid;
  }

  const isPaidValidated = toBooleanOrUndefined(record.isPaidValidated);
  if (typeof isPaidValidated === 'boolean') {
    user.isPaidValidated = isPaidValidated;
  }

  const validatedAt = toStringOrUndefined(record.validatedAt);
  if (validatedAt) {
    user.validatedAt = validatedAt;
  }

  const AudioContributionCount = toNumberOrUndefined(record.AudioContributionCount);
  if (typeof AudioContributionCount === 'number') {
    user.AudioContributionCount = AudioContributionCount;
  }

  const validatedAudioContributionCount = toNumberOrUndefined(record.validatedAudioContributionCount);
  if (typeof validatedAudioContributionCount === 'number') {
    user.validatedAudioContributionCount = validatedAudioContributionCount;
  }

  const confirmedContributionCount = toNumberOrUndefined(record.confirmedContributionCount);
  if (typeof confirmedContributionCount === 'number') {
    user.confirmedContributionCount = confirmedContributionCount;
  }

  const validationGate = toValidationGateOrUndefined(record.validationGate);
  if (validationGate) {
    user.validationGate = validationGate;
  }

  const geolocationConsent = toGeolocationConsent(record);
  if (geolocationConsent) {
    user.geolocationConsent = geolocationConsent;
  }

  return user;
};

const normalizeUser = (input: unknown): User | null => {
  if (!input) {
    return null;
  }

  if (isRecord(input)) {
    const direct = buildUserFromRecord(input);
    if (direct) {
      return direct;
    }

    for (const key of ['user', 'data', 'profile']) {
      if (key in input) {
        const nested = normalizeUser(input[key]);
        if (nested) {
          return nested;
        }
      }
    }
  }

  return null;
};

export function AppProvider({ children }: { children: ReactNode }) {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  
  // Language basée sur l'URL
  const currentLanguage: LanguageCode = (lang === 'fr' || lang === 'en') ? lang : 'fr';
  
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('yodi-theme');
      if (saved === 'dark' || saved === 'light') return saved;
      
      // Detect system preference
      if (window.matchMedia('(prefers-color-scheme: light)').matches) {
        return 'light';
      }
    }
    return 'dark';
  });

  const [user, setUserState] = useState<User | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    const saved = localStorage.getItem('yodi-user');
    if (!saved) {
      return null;
    }

    try {
      return normalizeUser(JSON.parse(saved));
    } catch {
      return null;
    }
  });

  const [hasAcceptedPolicy, setHasAcceptedPolicy] = useState(() => {
    if (typeof window !== 'undefined') {
      if (localStorage.getItem('yodi-policy-accepted') === 'true') {
        return true;
      }

      const saved = localStorage.getItem('yodi-user');
      if (saved) {
        try {
          const normalized = normalizeUser(JSON.parse(saved));
          return normalized?.isAcceptedTerms === true;
        } catch {
          return false;
        }
      }
    }
    return false;
  });

  // Initialize theme on mount
  React.useEffect(() => {
    const applyTheme = (newTheme: 'dark' | 'light') => {
      document.documentElement.classList.remove('dark', 'light');
      document.documentElement.classList.add(newTheme);
      
      // Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', newTheme === 'dark' ? '#0f172a' : '#ffffff');
      }
    };

    applyTheme(theme);
  }, [theme]);

  // Watch for system theme changes
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const saved = localStorage.getItem('yodi-theme');
      if (!saved) {
        // Only auto-switch if user hasn't manually set a preference
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const newTheme = current === 'dark' ? 'light' : 'dark';
      const applyThemeImmediately = () => {
        document.documentElement.classList.remove('dark', 'light');
        document.documentElement.classList.add(newTheme);
        document.querySelector('meta[name="theme-color"]')?.setAttribute(
          'content',
          newTheme === 'dark' ? '#0f172a' : '#ffffff',
        );
      };

      // Update the document before React re-renders the contribution workspace.
      // The transition keeps heavier recording screens responsive during the update.
      applyThemeImmediately();
      localStorage.setItem('yodi-theme', newTheme);
      return newTheme;
    });
  }, []);

  const handleSetLanguage = useCallback((lang: LanguageCode) => {
    // Naviguer vers la nouvelle langue en gardant la route actuelle
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(fr|en)/, '');
    navigate(`/${lang}${pathWithoutLang || ''}`);
  }, [navigate]);

  // Cached queries belong to the account that ran them (scope, figures,
  // lists): signing out or into another account in the same tab must not
  // show the previous account's answers, so the cache is emptied first.
  const queryClient = useQueryClient();
  const accountIdRef = useRef<string | null>(user?.id != null ? String(user.id) : null);
  const forgetPreviousAccount = useCallback(
    (nextId: string | number | null | undefined) => {
      const next = nextId != null ? String(nextId) : null;
      if (accountIdRef.current !== null && accountIdRef.current !== next) queryClient.clear();
      accountIdRef.current = next;
    },
    [queryClient],
  );

  const handleSetUser = useCallback((nextUser: User | null) => {
    if (nextUser) {
      const normalized = normalizeUser(nextUser);
      if (!normalized) {
        console.warn('Unable to normalize user payload:', nextUser);
        return;
      }
      forgetPreviousAccount(normalized.id);

      setUserState((current) =>
        current && JSON.stringify(current) === JSON.stringify(normalized) ? current : normalized,
      );
      localStorage.setItem('yodi-user', JSON.stringify(normalized));

      const persistedAcceptance = localStorage.getItem('yodi-policy-accepted') === 'true';
      const accepted = persistedAcceptance || normalized.isAcceptedTerms === true;
      setHasAcceptedPolicy(accepted);
      if (accepted) {
        localStorage.setItem('yodi-policy-accepted', 'true');
      }
    } else {
      forgetPreviousAccount(null);
      setUserState(null);
      sessionRefresher.end();
      localStorage.removeItem('yodi-user');
      localStorage.removeItem('yodi-token');
      setHasAcceptedPolicy(localStorage.getItem('yodi-policy-accepted') === 'true');
    }
  }, [forgetPreviousAccount]);

  const acceptPolicy = useCallback(() => {
    setHasAcceptedPolicy(true);
    localStorage.setItem('yodi-policy-accepted', 'true');
  }, []);

  // Register global session invalidation handler (called by apiClient on 401)
  useEffect(() => {
    setSessionInvalidationHandler((reason) => {
      handleSetUser(null);
      if (reason) {
        const lang = window.location.pathname.split('/')[1] || 'fr';
        window.location.replace(`/${lang}/account-status?status=${reason}`);
        return;
      }
      saveAuthReturnPath();
    });
    return () => setSessionInvalidationHandler(() => {});
  }, []);

  // Mettre à jour l'attribut lang du document
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const contextValue: AppContextValue = useMemo(() => ({
    theme,
    toggleTheme,
    language: currentLanguage,
    setLanguage: handleSetLanguage,
    t: translations[currentLanguage],
    user,
    setUser: handleSetUser,
    isAuthenticated: !!user,
    hasAcceptedPolicy,
    acceptPolicy,
  }), [theme, toggleTheme, currentLanguage, handleSetLanguage, user, handleSetUser, hasAcceptedPolicy, acceptPolicy]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
